import React from "react"
import { useGlobalState } from "./GlobalStateProvider"

export interface IPreviewFeature {
  name: string
  description: string
  /** ISO date (yyyy-mm-dd) the feature was put behind the flag. */
  addedOn: string
}

function definePreviewFeatures<T extends Record<string, IPreviewFeature>>(features: T): T {
  return features
}

/**
 * Registry of features currently hidden behind the "Can View Preview Features" permission.
 * Listed on System → Preview Features. See PreviewFeatures.md at the repo root for the rules on
 * what belongs here.
 *
 * To release a feature: delete its entry. Typescript then errors on every
 * `usePreviewFeature("key")` / `<PreviewFeature feature="key">` usage, which is the list of places
 * to unwrap. You can also grep for the key.
 */
export const previewFeatures = definePreviewFeatures({
  updatePurchaseOrdersInSage: {
    name: "Update purchase orders in Sage",
    description:
      "Purchase orders: changes made to a purchase order after it was exported (quantities, prices, added and removed lines, requested date and supplier document number) can be sent to the order in Sage with the Update in Sage button. Also needs the Can Update Purchase Orders in Sage permission.",
    addedOn: "2026-10-19",
  },
  barscanWarehouseTransferV2: {
    name: "Barscan warehouse transfer V2",
    description:
      "Branches: a Barscan setting to send internal order transfers using WarehouseTransferRequestV2, which also passes Barscan the order's created date, requested date and whether it is a SHUNT order request.",
    addedOn: "2026-10-19",
  },
})

export type PreviewFeatureKey = keyof typeof previewFeatures

/** True when the current user can see preview features. */
export function useCanViewPreviewFeatures() {
  const { currentUser } = useGlobalState()
  return !!currentUser.canViewPreviewFeatures
}

/** Hook form of <PreviewFeature>, for conditions that can't wrap JSX (menu items, columns...). */
export function usePreviewFeature(_feature: PreviewFeatureKey) {
  return useCanViewPreviewFeatures()
}

/** Renders children only for users with the preview features permission. */
export function PreviewFeature({
  children,
  fallback,
  feature,
}: {
  children: React.ReactNode
  fallback?: React.ReactNode
  feature: PreviewFeatureKey
}) {
  const canView = usePreviewFeature(feature)
  return <React.Fragment>{canView ? children : fallback ?? null}</React.Fragment>
}
